import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, Loader2, Mic, Play, ShieldAlert, Sparkles, UploadCloud } from "lucide-react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { getIsAdmin } from "@/lib/reports.functions";
import { listElevenLabsVoices, convertSpeechToSpeech } from "@/lib/voice-lab.functions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/admin_/voice-lab")({
  head: () => ({
    meta: [
      { title: "音声変換ラボ (Admin) | KAGEM" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: VoiceLabPage,
});

type Voice = {
  voice_id: string;
  name: string;
  preview_url: string | null;
  category: string | null;
  labels: Record<string, string>;
  description: string | null;
  verified_languages: Array<{ language?: string; locale?: string; accent?: string }>;
};

type ConversionResult = {
  audio_base64: string;
  mime: string;
  latency_ms: number;
  model_id: string;
  voice_id: string;
  cost_headers: Record<string, string>;
  input_bytes: number;
  output_bytes: number;
};

const STS_MODELS = [
  { id: "eleven_multilingual_sts_v2", label: "eleven_multilingual_sts_v2 (推奨・多言語)" },
  { id: "eleven_english_sts_v2", label: "eleven_english_sts_v2 (英語専用)" },
];

function isJapaneseVoice(v: Voice): boolean {
  const langs = (v.verified_languages ?? []).map(
    (l) => (l.language || l.locale || "").toLowerCase(),
  );
  const labelLang = (v.labels?.language || "").toLowerCase();
  const desc = (v.description || "").toLowerCase();
  return (
    langs.some((l) => l.startsWith("ja")) ||
    labelLang.startsWith("ja") ||
    labelLang.includes("japanese") ||
    desc.includes("japanese") ||
    desc.includes("日本")
  );
}

function VoiceLabPage() {
  const isAdminFn = useServerFn(getIsAdmin);
  const listFn = useServerFn(listElevenLabsVoices);
  const convertFn = useServerFn(convertSpeechToSpeech);

  const [checked, setChecked] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const [loadingVoices, setLoadingVoices] = useState(false);
  const [voices, setVoices] = useState<Voice[]>([]);
  const [total, setTotal] = useState(0);
  const [jaCount, setJaCount] = useState(0);
  const [filterJa, setFilterJa] = useState(false);
  const [search, setSearch] = useState("");

  const [selectedVoiceId, setSelectedVoiceId] = useState<string | null>(null);
  const [modelId, setModelId] = useState(STS_MODELS[0].id);

  const [file, setFile] = useState<File | null>(null);
  const [inputUrl, setInputUrl] = useState<string | null>(null);
  const [converting, setConverting] = useState(false);
  const [result, setResult] = useState<ConversionResult | null>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!data.session) {
          setIsAdmin(false);
          return;
        }
        const headers = { Authorization: `Bearer ${data.session.access_token}` };
        const res = await isAdminFn({ headers });
        setIsAdmin(res.isAdmin);
      } catch (e) {
        console.error(e);
        setIsAdmin(false);
      } finally {
        setChecked(true);
      }
    })();
  }, [isAdminFn]);

  const loadVoices = async () => {
    setLoadingVoices(true);
    try {
      const { data } = await supabase.auth.getSession();
      const headers = data.session
        ? { Authorization: `Bearer ${data.session.access_token}` }
        : undefined;
      const res = await listFn({ headers });
      setVoices(res.voices as Voice[]);
      setTotal(res.total);
      setJaCount(res.japanese_count);
    } catch (e) {
      console.error(e);
      toast.error("ボイス一覧の取得に失敗しました: " + (e as Error).message);
    } finally {
      setLoadingVoices(false);
    }
  };

  useEffect(() => {
    if (isAdmin) void loadVoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return voices.filter((v) => {
      if (filterJa && !isJapaneseVoice(v)) return false;
      if (!q) return true;
      const blob = [
        v.name,
        v.description ?? "",
        v.category ?? "",
        ...Object.values(v.labels ?? {}),
      ]
        .join(" ")
        .toLowerCase();
      return blob.includes(q);
    });
  }, [voices, search, filterJa]);

  const handleFile = (f: File | null) => {
    setResult(null);
    setFile(f);
    if (inputUrl) URL.revokeObjectURL(inputUrl);
    if (f) {
      if (f.size > 10 * 1024 * 1024) {
        toast.error("ファイルサイズは 10MB 以下にしてください");
        setFile(null);
        setInputUrl(null);
        return;
      }
      setInputUrl(URL.createObjectURL(f));
    } else {
      setInputUrl(null);
    }
  };

  const previewVoice = (url: string | null) => {
    if (!url) {
      toast.info("この voice にはプレビューがありません");
      return;
    }
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
    }
    const a = new Audio(url);
    previewAudioRef.current = a;
    void a.play();
  };

  const doConvert = async () => {
    if (!selectedVoiceId) return toast.error("ボイスを選択してください");
    if (!file) return toast.error("音声ファイルをアップロードしてください");
    setConverting(true);
    setResult(null);
    try {
      const { data } = await supabase.auth.getSession();
      const headers = data.session
        ? { Authorization: `Bearer ${data.session.access_token}` }
        : undefined;
      const fd = new FormData();
      fd.append("voice_id", selectedVoiceId);
      fd.append("model_id", modelId);
      fd.append("audio", file, file.name);
      const res = (await convertFn({ data: fd, headers })) as ConversionResult;
      setResult(res);
      toast.success(`変換完了 (${(res.latency_ms / 1000).toFixed(1)}s)`);
    } catch (e) {
      console.error(e);
      toast.error("変換に失敗しました: " + (e as Error).message);
    } finally {
      setConverting(false);
    }
  };

  if (!checked) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </main>
    );
  }
  if (!isAdmin) {
    return (
      <main className="min-h-screen bg-background px-4 py-16">
        <div className="mx-auto max-w-md">
          <Card className="p-8 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
              <ShieldAlert className="h-7 w-7 text-destructive" />
            </div>
            <h1 className="text-2xl font-bold">アクセス権がありません</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              この画面は管理者ロールを持つユーザーのみが利用できます。
            </p>
            <Button asChild className="mt-6">
              <Link to="/dashboard">ダッシュボードへ</Link>
            </Button>
          </Card>
        </div>
      </main>
    );
  }

  const outputUrl = result ? `data:${result.mime};base64,${result.audio_base64}` : null;

  return (
    <main className="min-h-screen bg-background px-4 py-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Link to="/admin" className="inline-flex items-center gap-1 hover:text-foreground">
                <ArrowLeft className="h-4 w-4" /> 管理画面に戻る
              </Link>
            </div>
            <h1 className="mt-1 text-3xl font-bold tracking-tight">
              <Mic className="mr-2 inline h-6 w-6" />
              音声変換ラボ (Admin)
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              ElevenLabs Speech-to-Speech の実接続テスト。ボイス品質・レイテンシ・日本語適性を検証します。
            </p>
          </div>
        </div>

        <Card className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm">
              <span className="font-semibold">取得ボイス</span>:{" "}
              <span className="tabular-nums">{total}</span> 件 / 日本語対応疑い{" "}
              <span className="tabular-nums">{jaCount}</span> 件
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                placeholder="ボイス名・ラベル検索"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 w-56"
              />
              <label className="flex items-center gap-1.5 text-sm">
                <input
                  type="checkbox"
                  checked={filterJa}
                  onChange={(e) => setFilterJa(e.target.checked)}
                />
                日本語のみ
              </label>
              <Button size="sm" variant="outline" onClick={loadVoices} disabled={loadingVoices}>
                {loadingVoices ? (
                  <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                ) : null}
                再取得
              </Button>
            </div>
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((v) => {
              const active = selectedVoiceId === v.voice_id;
              const ja = isJapaneseVoice(v);
              return (
                <button
                  key={v.voice_id}
                  type="button"
                  onClick={() => setSelectedVoiceId(v.voice_id)}
                  className={cn(
                    "rounded-lg border p-3 text-left transition-colors",
                    active
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/40",
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate font-semibold">{v.name}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {v.category ?? "—"}
                        {ja ? " · 🇯🇵 JA" : ""}
                      </p>
                    </div>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        previewVoice(v.preview_url);
                      }}
                      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border hover:bg-accent"
                      aria-label="プレビュー再生"
                    >
                      <Play className="h-3.5 w-3.5" />
                    </span>
                  </div>
                  {Object.keys(v.labels ?? {}).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {Object.entries(v.labels).slice(0, 4).map(([k, val]) => (
                        <span
                          key={k}
                          className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground"
                        >
                          {k}: {val}
                        </span>
                      ))}
                    </div>
                  )}
                </button>
              );
            })}
            {!loadingVoices && filtered.length === 0 && (
              <p className="col-span-full py-8 text-center text-sm text-muted-foreground">
                該当するボイスがありません
              </p>
            )}
          </div>
        </Card>

        <Card className="p-4">
          <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold">
            <Sparkles className="h-4 w-4" /> Speech-to-Speech 変換
          </h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-3">
              <div>
                <Label className="mb-1 block text-xs">選択中のボイス</Label>
                <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
                  {selectedVoiceId
                    ? voices.find((v) => v.voice_id === selectedVoiceId)?.name ?? selectedVoiceId
                    : "上のリストから選択してください"}
                </div>
              </div>
              <div>
                <Label className="mb-1 block text-xs">モデル</Label>
                <Select value={modelId} onValueChange={setModelId}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STS_MODELS.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1 block text-xs">
                  音声ファイル (mp3 / wav / m4a, ≤10MB)
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept="audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/mp4,audio/m4a,audio/x-m4a,.mp3,.wav,.m4a"
                    onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
                  />
                </div>
                {file && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {file.name} ({(file.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>
              <Button
                onClick={doConvert}
                disabled={converting || !file || !selectedVoiceId}
                className="w-full"
              >
                {converting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> 変換中...
                  </>
                ) : (
                  <>
                    <UploadCloud className="mr-2 h-4 w-4" /> この声に変換する
                  </>
                )}
              </Button>
            </div>

            <div className="space-y-3">
              <div>
                <Label className="mb-1 block text-xs">元の音声</Label>
                {inputUrl ? (
                  <audio controls src={inputUrl} className="w-full" />
                ) : (
                  <div className="rounded-md border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground">
                    ファイル未選択
                  </div>
                )}
              </div>
              <div>
                <Label className="mb-1 block text-xs">変換後の音声</Label>
                {outputUrl ? (
                  <audio controls src={outputUrl} className="w-full" />
                ) : (
                  <div className="rounded-md border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground">
                    未変換
                  </div>
                )}
              </div>
              {result && (
                <div className="rounded-md border border-border bg-muted/30 p-3 text-xs">
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                    <span className="text-muted-foreground">レイテンシ</span>
                    <span className="tabular-nums">
                      {(result.latency_ms / 1000).toFixed(2)} 秒
                    </span>
                    <span className="text-muted-foreground">モデル</span>
                    <span className="font-mono">{result.model_id}</span>
                    <span className="text-muted-foreground">入力 / 出力</span>
                    <span className="tabular-nums">
                      {(result.input_bytes / 1024).toFixed(1)} KB /{" "}
                      {(result.output_bytes / 1024).toFixed(1)} KB
                    </span>
                  </div>
                  {Object.keys(result.cost_headers).length > 0 && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-muted-foreground">
                        レスポンスヘッダ (cost/credit)
                      </summary>
                      <pre className="mt-1 overflow-auto text-[10px]">
                        {JSON.stringify(result.cost_headers, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>
    </main>
  );
}
